<script setup>
import { ref, computed } from 'vue'
import { getI18n } from '@/plugins/i18n'

import { useNotify } from '@/composables/notify'

import validationEngine from 'devextreme/ui/validation_engine'
import { DxForm } from 'devextreme-vue/form'

import TabbedGridDropdown from '@/components/shared/TabbedGridDropdown.vue'
import FileUploader from '@/components/shared/FileUploader.vue'

import * as dataGrid from '@/composables/data-grid'
import * as dataGridColumn from '@/composables/data-grid-column'
import { useLookup } from '@/composables/data-grid'

import { usePortalStore } from '@/stores/portal.store'
import { useGarageStore } from '@/stores/garage.store'
import { useWebSiteStore } from '@/stores/web-site.store'
import { useRegistryStore } from '@/stores/registry.store'
import { useOrderStore } from '@/stores/order.store'
import { useSaleStore } from '@/stores/sale.store'

const props = defineProps({
  formData: {
    type: Object,
    default: () => {}
  },
  isReadonly: {
    type: Boolean,
    default: false,
  },
  entitiesId: {
    type: Object,
    default: () => {}
  },
})
const emit = defineEmits(['uploaded'])

const i18n = getI18n()
const { notifyError } = useNotify()

const portalStore = usePortalStore()
const garageStore = useGarageStore()
const webSiteStore = useWebSiteStore()
const registryStore = useRegistryStore()
const orderStore = useOrderStore()
const saleStore = useSaleStore()

const files = ref([])
const fileCellInfo = {
  get value() { return files.value },
  setValue(newValue) { files.value = newValue },
}
const fileUploaderRef = ref(null)

const formData = ref({ ...props.formData })

const uploadProgress = ref([])
const isUploading = ref(false)

const formRef = ref(null)
const validationGroup = 'event-scheduler-form'

const currentUpload = computed(() => { return uploadProgress.value.find(x => x.percent < 100) || uploadProgress.value[uploadProgress.value.length - 1] })
const uploadedFiles = computed(() => { return uploadProgress.value.filter(x => x.percent >= 100).length })

const entityIsValid = ref(true)
const filesIsValid = ref(true)

const getTags = (subCategoryId) => {
  if (subCategoryId == null) return { store: [] }
  const allowedIds = portalStore.attachmentTagMap[subCategoryId] ?? []
  return {
    store: portalStore.getLocalizedAttachmentTags().filter(t => allowedIds.includes(t.id)),
  }
}

const getSubCategories = (categoryId = formData.value.attachment_category) => {
  if (categoryId == null) return { store: [] }
  const allowedIds = portalStore.attachmentCategoryMap[categoryId] ?? []
  return {
    store: portalStore.getLocalizedAttachmentSubcategories().filter(c => allowedIds.includes(c.id)),
  }
}

const updateEditorOptions = (dataField, value, getDataSource) => {
  formRef.value?.instance?.getEditor(dataField)?.option({
    dataSource: getDataSource(value),
    disabled: value == null,
  })
}

const formItems = [
  {
    itemType: 'group',
    colCount: 3,
    items: [
      {
        dataField: 'attachment_category',
        editorType: 'dxSelectBox',
        editorOptions: {
          ...useLookup({ dataSource: portalStore.getLocalizedAttachmentCategories() }),
          onValueChanged: (e) => {
            formData.value.attachment_subcategory = null
            formData.value.attachment_tag = null
            formData.value.entity = null
            updateEditorOptions('attachment_subcategory', e.value, getSubCategories)
            updateEditorOptions('attachment_tag', null, getTags)
          },
          disabled: props.isReadonly,
        },
        label: { text: i18n.t('common.attachmentCategory') },
        validationRules: [{ type: 'required' }],
      },
      {
        dataField: 'attachment_subcategory',
        editorType: 'dxSelectBox',
        editorOptions: {
          ...useLookup({ dataSource: getSubCategories() }),
          onValueChanged: (e) => {
            formData.value.attachment_tag = null
            formData.value.entity = null
            updateEditorOptions('attachment_tag', e.value, getTags)
          },
          disabled: formData.value.attachment_category == null,
        },
        label: { text: i18n.t('common.attachmentSubcategory') },
        validationRules: [{ type: 'required' }],
      },
      {
        dataField: 'entity',
        template: 'entityTemplate',
        label: { text: i18n.t('common.document') },
        validationRules: [{ type: 'required' }],
      },
      {
        dataField: 'attachment_tag',
        editorType: 'dxSelectBox',
        editorOptions: {
          ...useLookup({ dataSource: getTags(formData.value.attachment_subcategory) }),
          disabled: formData.value.attachment_subcategory == null,
        },
        label: { text: i18n.t('common.tag') },
        validationRules: [{ type: 'required' }],
      },
      {
        dataField: 'attachment_description',
        label: { text: i18n.t('common.description') },
        colSpan: 2,
      },
    ],
  },
]

const allEntitiesTabs = [
  {
    id: 'quotes',
    title: i18n.t('common.documentType.quote', 2),
    columns: [
      { dataField: 'series', caption: i18n.t('common.series'), width: '6rem' },
      { dataField: 'number_mnemonic', caption: i18n.t('common.number'), width: '4rem', sortOrder: 'desc' },
      { dataField: 'vehicle_id', caption: i18n.t('common.vehicle'), lookup: useLookup({ store: registryStore.vehicles }) },
      { dataField: 'customer_id', caption: i18n.t('common.customer'), lookup: useLookup({ store: registryStore.customers }) },
      { dataField: 'date', caption: i18n.t('common.date'), sortOrder: 'desc' },
    ],
    dataSourceUrl: garageStore.api.getAbsoluteUrl() + garageStore.api.quotesUrl,
    getDisplay: (item) => `${item.series} - ${item.number_mnemonic}`,
    categories: [portalStore.attachmentCategoryEnum.GARAGE_DOCUMENT],
    subCategories: [portalStore.attachmentSubcategoryEnum.DOCUMENT],
    fieldName: 'attachment_quote_id',
  },
  {
    id: 'worksheets',
    title: i18n.t('common.documentType.worksheet', 2),
    columns: [
      { dataField: 'series', caption: i18n.t('common.series'), width: '6rem' },
      { dataField: 'number_mnemonic', caption: i18n.t('common.number'), width: '4rem', sortOrder: 'desc' },
      { dataField: 'vehicle_id', caption: i18n.t('common.vehicle'), lookup: useLookup({ store: registryStore.vehicles }) },
      { dataField: 'customer_id', caption: i18n.t('common.customer'), lookup: useLookup({ store: registryStore.customers }) },
      { dataField: 'date', caption: i18n.t('common.date'), sortOrder: 'desc' },
    ],
    dataSourceUrl: garageStore.api.getAbsoluteUrl() + garageStore.api.worksheetsUrl,
    getDisplay: (item) => `${item.series} - ${item.number_mnemonic}`,
    categories: [portalStore.attachmentCategoryEnum.GARAGE_DOCUMENT],
    subCategories: [portalStore.attachmentSubcategoryEnum.DOCUMENT],
    fieldName: 'attachment_worksheet_id',
  },
  {
    id: 'supplier_order',
    title: i18n.t('common.documentType.supplier_order', 2),
    columns: [
      { dataField: 'series', caption: i18n.t('common.series'), width: '6rem' },
      { dataField: 'number_mnemonic', caption: i18n.t('common.number'), width: '4rem', sortOrder: 'desc' },
      { dataField: 'supplier_name', caption: i18n.t('common.name') },
      { dataField: 'date', caption: i18n.t('common.date'), sortOrder: 'desc' },
    ],
    dataSourceUrl: orderStore.api.getAbsoluteUrl() + orderStore.api.supplierOrdersUrl,
    getDisplay: (item) => `${item.series} - ${item.number_mnemonic}`,
    categories: [portalStore.attachmentCategoryEnum.ORDER],
    subCategories: [portalStore.attachmentSubcategoryEnum.DOCUMENT],
    fieldName: 'attachment_supplierorder_id',
  },
  {
    id: 'sale_document',
    title: i18n.t('common.saleDocument', 2),
    columns: [
      { dataField: 'series', caption: i18n.t('common.series'), width: '6rem' },
      { dataField: 'number_mnemonic', caption: i18n.t('common.number'), width: '4rem', sortOrder: 'desc' },
      dataGridColumn.useSaleDocumentTypeColumn({ width: '8rem' }),
      { dataField: 'customer_id', caption: i18n.t('common.customer'), lookup: useLookup({ store: registryStore.customers }) },
      { dataField: 'date', caption: i18n.t('common.date'), sortOrder: 'desc' },
    ],
    dataSourceUrl: saleStore.api.getAbsoluteUrl() + saleStore.api.documentsUrl,
    getDisplay: (item) => `${item.series} - ${item.number_mnemonic}`,
    categories: [portalStore.attachmentCategoryEnum.SALE_DOCUMENT],
    subCategories: [portalStore.attachmentSubcategoryEnum.DOCUMENT],
    fieldName: 'attachment_saledocument_id',
  },
  {
    id: 'vehicle',
    title: i18n.t('common.vehicle', 2),
    columns: [
      { dataField: 'code', caption: i18n.t('common.protocol'), width: '4rem' },
      { dataField: 'vin', caption: i18n.t('common.vin') },
      { dataField: 'plate_number', caption: i18n.t('common.plate'), width: '8rem' },
      dataGridColumn.useSupplierBrandColumn(),
      { dataField: 'customer_id', caption: i18n.t('common.customer'), lookup: useLookup({ store: registryStore.customers }) },
    ],
    dataSourceUrl: registryStore.api.getAbsoluteUrl() + registryStore.api.vehiclesUrl,
    getDisplay: (item) => `(${item.code}) ${item.plate_number} - ${item.vin}`,
    categories: [portalStore.attachmentCategoryEnum.GARAGE_DOCUMENT, portalStore.attachmentCategoryEnum.VEHICLE],
    subCategories: [portalStore.attachmentSubcategoryEnum.VEHICLE],
    fieldName: 'attachment_vehicle_id',
  },
  {
    id: 'subject',
    title: i18n.t('common.subject', 2),
    columns: [
      { dataField: 'type', caption: i18n.t('common.subjectSubType'), cellTemplate: 'typeCellTemplate', lookup: dataGrid.useLookup({ store: registryStore.getLocalizedSubjectEntityTypes(), displayExpr: 'name' }) },
      { dataField: 'code', caption: i18n.t('common.code'), width: '4rem' },
      { dataField: 'name', caption: i18n.t('common.name') },
      { dataField: 'name_ext', caption: i18n.t('common.nameExt') },
      { dataField: 'mobile', caption: i18n.t('common.mobile') },
      { dataField: 'tax_code', caption: i18n.t('common.taxCode') },
    ],
    dataSourceUrl: registryStore.api.getAbsoluteUrl() + registryStore.api.subjectsUrl,
    getDisplay: (item) => `(${item.code}) ${item.name} - ${item.name_ext}`,
    categories: [portalStore.attachmentCategoryEnum.SUBJECT, portalStore.attachmentCategoryEnum.CONTRACT, portalStore.attachmentCategoryEnum.SALE_DOCUMENT, portalStore.attachmentCategoryEnum.GARAGE_DOCUMENT, portalStore.attachmentCategoryEnum.ORDER, portalStore.attachmentCategoryEnum.VEHICLE],
    subCategories: [portalStore.attachmentSubcategoryEnum.SUBJECT],
    fieldName: 'attachment_subject_ref',
  },
]

const createDataSource = (tab, entityId) => dataGrid.useODataSource(tab.dataSourceUrl, entityId != null ? { filter: ['id', '=', entityId] } : undefined)

const entitiesTabs = computed(() => {
  const category = formData.value.attachment_category
  const subCategory = formData.value.attachment_subcategory

  if (category === null) { return [] }

  const hasEntitiesId = props.entitiesId && Object.keys(props.entitiesId).length > 0

  return allEntitiesTabs.filter(tab => tab.categories.includes(category) && tab.subCategories.includes(subCategory) && (!hasEntitiesId || tab.id in props.entitiesId))
    .map(tab => ({
      ...tab,
      dataSource: createDataSource(tab, props.entitiesId?.[tab.id]?.entityId),
    }))
})

const validationForm = () => {
  const form = formRef.value?.instance
  if (!form) return

  entityIsValid.value = formData.value.entity != null
  return validationEngine.validateGroup(validationGroup)
}

const uploadFiles = async() => {
  const validationResult = validationForm()
  filesIsValid.value = files.value.length > 0
  if (!validationResult?.isValid || !entityIsValid.value || !filesIsValid.value) return

  const data = { ...formData.value }
  data[data.entity.fieldName] = data.entity.id
  data['attachment_user_name'] = webSiteStore.user.name
  delete data.entity

  isUploading.value = true
  uploadProgress.value = files.value.map(f => ({ name: f.name, percent: 0 }))

  let hasError = false

  for (let i = 0; i < files.value.length; i++) {
    const { name, payload } = files.value[i]
    try {
      const blob = await fetch(payload).then(r => r.blob())
      const file = new File([blob], name, { type: blob.type })
      await registryStore.api.uploadServiceAttachmentAsync(data, file, {
        onUploadProgress: (event) => {
          uploadProgress.value[i].percent = Math.round((event.loaded * 100) / event.total)
        }
      })
    } catch (error) {
      hasError = true
      const localizationKey = error?.response?.data?.localization
      notifyError(localizationKey ? i18n.t(localizationKey) : name)
    }
  }

  isUploading.value = false
  uploadProgress.value = []

  if (hasError) return
  resetForm()
  emit('uploaded')
}

const resetForm = () => {
  Object.keys(formData.value).forEach(key => { formData.value[key] = null })
  updateEditorOptions('attachment_subcategory', null, getSubCategories)
  updateEditorOptions('attachment_tag', null, getTags)
  files.value = []
  filesIsValid.value = true
  entityIsValid.value = true
  fileUploaderRef.value?.resetFiles()
}

defineExpose({ resetForm, uploadFiles })
</script>

<template>
  <div>
    <div
      v-if="isUploading"
      class="absolute-full flex flex-center"
      style="background: rgba(255,255,255,0.65); backdrop-filter: blur(6px); z-index: 1000;"
    >
      <q-card
        style="width: 100%; max-width: 500px;"
      >
        <q-card-section class="row justify-between items-start">
          <div class="row items-center">
            <q-spinner
              color="primary"
              size="30px"
              class="q-mr-md"
            />
            <div class="text-caption text-grey-7">
              {{ currentUpload?.name }}
            </div>
          </div>

          <div>{{ uploadedFiles }}/{{ uploadProgress.length }}</div>
        </q-card-section>

        <q-card-section>
          <div class="row justify-between q-mb-xs">
            <div class="text-body2">
              {{ currentUpload?.percent || 0 }}%
            </div>
          </div>

          <q-linear-progress
            :value="(currentUpload?.percent || 0) / 100"
            rounded
            size="12px"
            color="primary"
            track-color="grey-3"
            animation-speed="250"
          />
        </q-card-section>
      </q-card>
    </div>
    <div class="row q-gutter-md items-start">
      <div class="col-3 q-pa-xs">
        <file-uploader
          ref="fileUploaderRef"
          :cell-info="fileCellInfo"
          :options="{ allow_download: false }"
          :validation-error="{ message: i18n.t('common.required') }"
          :is-valid="filesIsValid"
        />
      </div>

      <div class="col">
        <dx-form
          ref="formRef"
          :form-data="formData"
          :items="formItems"
          :validation-group="validationGroup"
        >
          <template #entityTemplate>
            <tabbed-grid-dropdown
              :tabs="entitiesTabs"
              :value="formData.entity"
              :disabled="formData?.attachment_subcategory == null"
              :is-valid="entityIsValid"
              :validation-error="{ message: i18n.t('common.required') }"
              :entity-drop-down-opened="formData?.attachment_subcategory != null && !!props.entitiesId"
              @value-changed="(value) => { formData.entity = value; validationForm() }"
            />
          </template>
        </dx-form>
      </div>
    </div>
  </div>
</template>
